import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { motion } from "framer-motion";
import { ShieldCheck, ScrollText, Copyright } from "lucide-react";

const EFFECTIVE = "June 2026";
const CONTACT = "hello@narrativault.io";

function LegalShell({ icon: Icon, eyebrow, title, accent, children }) {
  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Header />
      <main className="max-w-3xl mx-auto px-5 md:px-8 py-16 md:py-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-white/70">
            <Icon className="h-3.5 w-3.5 text-[#00F0FF]" /> {eyebrow}
          </span>
          <h1 className="font-display mt-6 text-4xl sm:text-5xl font-extrabold tracking-tighter">
            {title} <span className="text-[#00F0FF]">{accent}</span>
          </h1>
          <p className="mt-4 text-sm text-white/40">Effective {EFFECTIVE}</p>
        </motion.div>

        <div className="mt-12 space-y-10 legal-body">{children}</div>

        <div className="mt-16 border-t border-white/10 pt-6 text-sm text-white/50">
          Questions? Email us at{" "}
          <a href={`mailto:${CONTACT}`} className="text-[#00F0FF] hover:underline">{CONTACT}</a>.
        </div>
      </main>

      <footer className="border-t border-white/10 py-8">
        <div className="max-w-7xl mx-auto px-5 md:px-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-white/40">
          <Link to="/" className="font-display font-semibold text-white">Narrative<span className="text-[#00F0FF]">Vault</span></Link>
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
            <Link to="/copyright" className="hover:text-white transition-colors">Copyright</Link>
            <span>© {new Date().getFullYear()} — by WTN Productions</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Section({ heading, children }) {
  return (
    <section>
      <h2 className="font-display text-lg font-semibold text-white mb-3">{heading}</h2>
      <div className="text-white/60 text-sm leading-relaxed space-y-3">{children}</div>
    </section>
  );
}

export function PrivacyPolicy() {
  return (
    <LegalShell icon={ShieldCheck} eyebrow="Your data, protected" title="Privacy" accent="Policy">
      <p className="text-white/60 text-sm leading-relaxed">
        This Privacy Policy explains what information Narrative Vault ("we", "us", operated by WTN Productions)
        collects, how we use it, who we share it with, and how we keep it secure. By using narrativault.io you
        agree to the practices described here.
      </p>

      <Section heading="Information we collect">
        <p><strong className="text-white/80">Account information:</strong> name, email address, and password (stored only as a secure hash), or profile details from Google if you sign in with Google.</p>
        <p><strong className="text-white/80">Content:</strong> the videos, images, event details, and pricing you upload to your vaults.</p>
        <p><strong className="text-white/80">Client activity:</strong> when a client views, unlocks, purchases, or downloads a clip, we record the client's email and the associated event/clip so access can be granted.</p>
        <p><strong className="text-white/80">Payment information:</strong> payments are processed by Stripe. We never see or store your full card details — Stripe handles that directly and securely.</p>
        <p><strong className="text-white/80">Technical data:</strong> basic usage and device information needed to operate and secure the service.</p>
      </Section>

      <Section heading="How we use your information">
        <p>We use your information to: provide and operate the platform; host and deliver your videos; process payments and creator payouts; send transactional and account emails (via Resend); prevent fraud and abuse; and improve the service.</p>
      </Section>

      <Section heading="Who we share it with">
        <p>We share information only with the service providers required to run the platform:</p>
        <p><strong className="text-white/80">Stripe</strong> (payments, subscriptions, and Connect payouts), <strong className="text-white/80">Resend</strong> (transactional email), our <strong className="text-white/80">cloud hosting and object-storage providers</strong> (to store and stream your files), and <strong className="text-white/80">Cloudflare</strong> (DNS and delivery). We do not sell your personal information.</p>
      </Section>

      <Section heading="Data security">
        <p>Passwords are stored as one-way hashes, payment data is handled by Stripe, and vaults are private — accessible only via the unique link you generate. We use industry-standard measures to safeguard the information in our systems.</p>
      </Section>

      <Section heading="Data retention & deletion">
        <p>We retain your content while your account is active. Vaults created on the free Starter plan automatically expire and are deleted 30 days after creation. You may request deletion of your account and associated data at any time by emailing us.</p>
      </Section>

      <Section heading="Your rights">
        <p>You may access, correct, export, or delete your personal information. To exercise any of these rights, contact us at {CONTACT}.</p>
      </Section>

      <Section heading="Changes to this policy">
        <p>We may update this policy from time to time. Material changes will be reflected by updating the effective date above.</p>
      </Section>
    </LegalShell>
  );
}

export function TermsOfService() {
  return (
    <LegalShell icon={ScrollText} eyebrow="The ground rules" title="Terms of" accent="Service">
      <p className="text-white/60 text-sm leading-relaxed">
        These Terms of Service govern your use of Narrative Vault (narrativault.io), operated by WTN Productions.
        By creating an account or using the platform, you agree to these terms.
      </p>

      <Section heading="1. The service">
        <p>Narrative Vault is a platform that lets videographers and content creators upload their own footage into private "event vaults" and sell or deliver access to their own clients. It is not a general file-hosting or file-sharing service.</p>
      </Section>

      <Section heading="2. Your account">
        <p>You are responsible for keeping your login credentials secure and for all activity under your account. You must provide accurate information and be at least the age of majority in your jurisdiction.</p>
      </Section>

      <Section heading="3. Your content & ownership">
        <p>You retain all ownership of the content you upload. Narrative Vault does not claim ownership of your content. You grant us a limited, non-exclusive, royalty-free license to host, store, process, transmit, and display your content solely to operate and provide the service (including delivering it to the clients you choose). This license ends when you delete the content or your account, except for copies retained in routine backups for a limited period.</p>
      </Section>

      <Section heading="4. Your representations & responsibility for uploads">
        <p>You are solely responsible for the content you upload. By uploading, you represent and warrant that:</p>
        <p>• You own the content or have all necessary rights, licenses, and permissions to upload, distribute, and sell it.</p>
        <p>• Your content does not infringe any copyright, trademark, privacy, publicity, or other rights of any third party, and does not violate any law.</p>
        <p>• <strong className="text-white/80">You have obtained all necessary permissions, releases, and rights required to upload and share any content through Narrative Vault, including permission and model/property releases from clients and any individuals appearing in the footage, where applicable.</strong></p>
        <p>• You will not upload content that is illegal, harmful, defamatory, malicious, or that you are not authorized to distribute.</p>
      </Section>

      <Section heading="5. Acceptable use">
        <p>You may not use the platform to store or distribute unlawful, infringing, or abusive material, or material you do not have the rights to. We may remove content or suspend accounts that violate these terms, at our discretion.</p>
      </Section>

      <Section heading="6. Copyright & DMCA">
        <p>We respect intellectual property rights and comply with the U.S. Digital Millennium Copyright Act (DMCA). If you believe content on Narrative Vault infringes your copyright, please follow the takedown process in our <Link to="/copyright" className="text-[#00F0FF] hover:underline">Copyright Policy</Link>. We will promptly remove or disable access to allegedly infringing content when appropriate and will terminate the accounts of repeat infringers.</p>
      </Section>

      <Section heading="7. Plans, fees & commission">
        <p>Paid subscription plans (Pro, Studio, Business) are billed monthly through Stripe and renew automatically until cancelled. On each clip sale, Narrative Vault retains a 5% platform commission (which covers payment-processing costs) and the remaining ~95% is paid to the creator. Free Starter-plan vaults expire 30 days after creation. You are responsible for any taxes on your earnings.</p>
      </Section>

      <Section heading="8. Payments & payouts">
        <p>Payments and creator payouts are handled by Stripe, including Stripe Connect for splitting clip sales. By accepting payouts you also agree to Stripe's Connected Account Agreement.</p>
      </Section>

      <Section heading="9. Content removal & account suspension">
        <p>We reserve the right, at our sole discretion and without prior notice, to remove or disable access to any content, and to suspend or terminate any account, that we believe violates these terms, our policies, or applicable law, or that exposes us or others to liability.</p>
      </Section>

      <Section heading="10. Disclaimers">
        <p>The service is provided "AS IS" and "AS AVAILABLE" without warranties of any kind, express or implied, including merchantability, fitness for a particular purpose, and non-infringement. We do not warrant that the service will be uninterrupted, secure, or error-free.</p>
      </Section>

      <Section heading="11. Limitation of liability">
        <p>To the maximum extent permitted by law, WTN Productions and Narrative Vault will not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of data, profits, or revenue, arising out of or related to your use of the service. Our total aggregate liability for any claim will not exceed the greater of the amounts you paid us in the 12 months before the claim or USD $100.</p>
      </Section>

      <Section heading="12. Indemnification">
        <p>You agree to defend, indemnify, and hold harmless WTN Productions, Narrative Vault, and their officers, employees, and agents from and against any claims, damages, liabilities, losses, and expenses (including reasonable legal fees) arising out of or related to: (a) content you upload or distribute; (b) your violation of these terms; or (c) your infringement of any third party's rights. In other words, if content you upload results in a claim against us because you did not have the rights to it, you agree to cover our resulting losses.</p>
      </Section>

      <Section heading="13. Termination">
        <p>We may suspend or terminate accounts that violate these terms. You may stop using the service and delete your account at any time.</p>
      </Section>

      <Section heading="14. Governing law">
        <p>These terms are governed by the laws of the State of Illinois, USA, without regard to its conflict-of-laws rules. You agree that any dispute will be subject to the exclusive jurisdiction of the state and federal courts located in Illinois.</p>
      </Section>

      <Section heading="15. Changes to these terms">
        <p>We may update these terms from time to time. Continued use of the service after changes take effect constitutes acceptance of the updated terms.</p>
      </Section>
    </LegalShell>
  );
}

export function CopyrightPolicy() {
  return (
    <LegalShell icon={Copyright} eyebrow="Respecting IP" title="Copyright" accent="Policy">
      <p className="text-white/60 text-sm leading-relaxed">
        Narrative Vault (operated by WTN Productions) respects the intellectual property rights of others and
        complies with the U.S. Digital Millennium Copyright Act (DMCA). This policy explains how to report
        content you believe infringes your copyright and how we handle those reports.
      </p>

      <Section heading="Reporting alleged infringement (DMCA takedown)">
        <p>If you believe content on Narrative Vault infringes a copyright you own or control, send a written notice to our designated copyright agent at <a href="mailto:hello@narrativault.io" className="text-[#00F0FF] hover:underline">hello@narrativault.io</a> with the subject line "DMCA Takedown".</p>
        <p>Your notice must include:</p>
        <p>1. Your physical or electronic signature.</p>
        <p>2. Identification of the copyrighted work you claim has been infringed.</p>
        <p>3. Identification of the material you claim is infringing and enough information for us to locate it (e.g., the vault link or clip URL).</p>
        <p>4. Your contact information (name, address, email, phone).</p>
        <p>5. A statement that you have a good-faith belief the use is not authorized by the copyright owner, its agent, or the law.</p>
        <p>6. A statement, under penalty of perjury, that the information in your notice is accurate and that you are the copyright owner or authorized to act on the owner's behalf.</p>
      </Section>

      <Section heading="Our response">
        <p>Upon receiving a valid notice, we will promptly remove or disable access to the allegedly infringing material and, where appropriate, notify the user who uploaded it.</p>
      </Section>

      <Section heading="Counter-notification">
        <p>If your content was removed and you believe it was removed in error or that you have the right to use it, you may submit a counter-notification to <a href="mailto:hello@narrativault.io" className="text-[#00F0FF] hover:underline">hello@narrativault.io</a> including: your signature; identification of the removed material and its prior location; a statement under penalty of perjury that you have a good-faith belief the material was removed by mistake or misidentification; and your consent to the jurisdiction of the federal court in Illinois.</p>
      </Section>

      <Section heading="Repeat infringers">
        <p>We will, in appropriate circumstances, suspend or terminate the accounts of users who are determined to be repeat infringers.</p>
      </Section>

      <Section heading="Designated agent">
        <p>DMCA notices should be directed to our designated copyright agent at <a href="mailto:hello@narrativault.io" className="text-[#00F0FF] hover:underline">hello@narrativault.io</a>. (We recommend registering a DMCA agent with the U.S. Copyright Office to strengthen safe-harbor protection.)</p>
      </Section>
    </LegalShell>
  );
}
